import { useReactiveVar } from "@apollo/client";
import type { LocationDescriptor } from "history";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { TFLocationState } from "types/topicflow";

import { appVersionVar } from "@cache/cache";

export type toObjectProps = {
  pathname: string;
  search?: string;
  state?: TFLocationState;
};

let trackedVersion: null | string = null;

export function useLink() {
  const history = useHistory();
  const latestAppVersion = useReactiveVar(appVersionVar);

  const redirect = useCallback(
    (to: string | toObjectProps, ignoredRefresh = false) => {
      // tracked version is empty on initial load
      if (!trackedVersion) {
        trackedVersion = latestAppVersion;
      }
      // We show an `a` tag when the app version has changed
      // so we can load a new build of the frontend app from server
      // https://github.com/ParmAngTech/manageros/issues/95
      else if (
        latestAppVersion &&
        latestAppVersion !== trackedVersion &&
        !ignoredRefresh
      ) {
        const isStrTo = typeof to === "string";
        const isLocationTo = to instanceof Location;
        const href = isStrTo ? to : to.pathname;
        if (!isStrTo && !isLocationTo && !to.state?.background) {
          return (window.location.href = href);
        }
      }
      history.push(to);
    },
    [history, latestAppVersion],
  );

  const replace = useCallback(
    (to: LocationDescriptor<unknown>) => {
      history.replace(to);
    },
    [history],
  );

  return {
    redirect,
    replace,
  };
}
